/**
 * ProtectedDeleteDialog - Confirmation dialog for deleting orders
 * that are on_route or delivered. Requires admin password re-entry.
 */
import React, { useState, useLayoutEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Loader2 } from 'lucide-react';

const ProtectedDeleteDialog = ({ isOpen, onClose, onConfirm, order, opening }) => {
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const identity = opening || order;
  const latest = useRef(null); latest.current = { identity, isOpen };
  const mounted = useRef(false);
  const pending = useRef(null);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useLayoutEffect(() => {
    setPassword(''); setError(''); setIsSubmitting(false);
    pending.current = null;
  }, [identity, isOpen]);

  if (!isOpen || !order) return null;

  const statusLabel = order.status === 'delivered' ? 'delivered' : 'assigned to a run';

  const handleConfirm = async () => {
    if (pending.current || !mounted.current || latest.current.identity !== identity || !latest.current.isOpen) return;
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    const attempt = {};
    pending.current = attempt;
    const owns = () => mounted.current && latest.current.identity === identity && latest.current.isOpen && pending.current === attempt;
    setIsSubmitting(true);
    setError('');
    try {
      const result = await onConfirm(order.id, password);
      if (owns() && (!result || result.state === 'accepted')) onClose();
    } catch (e) {
      if (owns()) setError(e.message || 'Failed to delete');
    } finally {
      if (owns()) { pending.current = null; setIsSubmitting(false); setPassword(''); }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="protected-delete-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-stone-900">Delete Protected Order</h3>
              <p className="text-sm text-stone-500 mt-1">
                This order for <span className="font-medium text-stone-700">{order.recipient_name || 'Unknown'}</span> is currently <span className="font-medium text-stone-700">{statusLabel}</span>. Enter your password to confirm deletion.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Input
              type="password"
              placeholder="Enter your password"
              value={password}
              disabled={isSubmitting}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              autoFocus
              data-testid="protected-delete-password"
            />
            {error && <p className="text-xs text-red-600 mt-1.5" data-testid="protected-delete-error">{error}</p>}
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 bg-stone-50 border-t border-stone-200">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="protected-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={isSubmitting || !password.trim()}
            data-testid="protected-delete-confirm"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Delete Order
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProtectedDeleteDialog;
